import { api } from '@goaltree/backend/convex/_generated/api'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import {
  ArrowLeft,
  BarChart3,
  Bell,
  ClipboardList,
  Compass,
  Flame,
  Gift,
  Globe,
  GlobeIcon,
  LifeBuoy,
  LogOutIcon,
  Mic,
  MonitorIcon,
  MoonIcon,
  Plus,
  Search,
  Settings,
  Sparkles,
  SunIcon,
  Target,
  Trophy,
  UserPlus,
  Users,
} from 'lucide-react'
import { type CSSProperties, type ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AccentRotateSwitch, AccentSwatches, useAccentPicker } from '@/components/accent-swatches'
import { CanopyAvatar } from '@/components/canopy-avatar'
import { CelebrationChips } from '@/components/celebration-picker'
import { CoachLauncherFace } from '@/components/coach-avatar'
import { CoachHeaderButton } from '@/components/coach-header-button'
import { CoachingBadge, SwitchModeButton } from '@/components/coaching/coaching-mode'
import { useCoachInbox } from '@/components/coaching/my-coach-notes'
import { GoProCard } from '@/components/go-pro-card'
import { IconTip } from '@/components/icon-tip'
import { InviteSheet } from '@/components/invite-banner'
import { LeaderboardUserSheet } from '@/components/leaderboard-user-sheet'
import { NewMenu } from '@/components/new-menu'
import { HeaderSubtitle, OpenAppButton } from '@/components/site-header'
import { StreakFlame } from '@/components/streak-icons'
import { useTheme } from '@/components/theme-provider'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { useCoachNavMeta } from '@/hooks/use-coach-nav-meta'
import { useFloatingHeader } from '@/hooks/use-floating-header'
import { useGoals } from '@/hooks/use-goals'
import { useIsPro } from '@/hooks/use-is-pro'
import { useReviewDue } from '@/hooks/use-review-due'
import { useTitleInset } from '@/hooks/use-title-inset'
import i18n, { LANG_STORAGE_KEY, SUPPORTED_LANGS } from '@/i18n'
import { authClient } from '@/lib/auth-client'
import { useIsCoaching } from '@/lib/coaching-mode'
import { clearOfflineData } from '@/lib/offline-cache'
import { optimisticSetLocale } from '@/lib/prefs-optimistic'
import { clearSessionIndicator } from '@/lib/session-indicator'
import { openCapture } from '@/store/capture.store'
import { useHeaderStore } from '@/store/header.store'
import { useUserMode } from '@/store/user-mode.store'

const HELP_URL = 'mailto:hello@goaltree.ai?subject=Goaltree%20feedback'

const LANG_LABELS: Record<string, string> = {
  en: 'EN',
  ja: '日本語',
  es: 'ES',
  fr: 'FR',
  pt: 'PT',
  zh: '中文',
}

interface MobileScreenHeaderProps {
  title: string
  showInbox?: boolean
  showSearch?: boolean
  showCoach?: boolean
  rightSlot?: ReactNode
  // Extra screen-specific action buttons rendered before the default icons.
  actions?: ReactNode
  // Actions placed beside the avatar and before notifications.
  leftActions?: ReactNode
  onClose?: () => void
  onBack?: () => void
  transparent?: boolean
  // Float over the content without the light-on-media styling (defaults to `transparent`).
  overlay?: boolean
  // Pull the back/close button tight to the edge (matches a right cluster that
  // also hugs the edge, e.g. the goal-detail header's star/3-dots).
  tightBack?: boolean
  // Content column the header's edges align with (CSS length; defaults to the page column).
  col?: string
}

export function MobileScreenHeader({
  title,
  showInbox = true,
  showSearch = true,
  showCoach = false,
  onClose,
  onBack,
  rightSlot,
  actions,
  leftActions,
  transparent = false,
  overlay = transparent,
  col = 'var(--page-col)',
  tightBack = false,
}: MobileScreenHeaderProps) {
  const { t } = useTranslation()
  const { subtitle, scrolled, raised } = useHeaderStore()
  const headerRef = useRef<HTMLElement>(null)
  useFloatingHeader(headerRef, '--app-hdr-m', !overlay)
  const titleInset = useTitleInset(headerRef, scrolled)
  const { isAuthenticated } = useConvexAuth()
  const user = useQuery(api.auth.getCurrentUser, isAuthenticated ? {} : 'skip')
  const unreadCount = useQuery(api.notifications.unreadCount, isAuthenticated ? {} : 'skip') ?? 0
  const setLocale = useMutation(api.preferences.setLocale).withOptimisticUpdate(optimisticSetLocale)
  const goals = useGoals()
  const coachMeta = useCoachNavMeta()
  const clientCount = useQuery(api.coaching.myClientCount) ?? 0
  const coaching = useIsCoaching()
  const hasInbox = goals?.some((g) => g.isInbox) ?? false
  const navigate = useNavigate()
  const { location } = useRouterState()
  const currentPath = location.pathname
  const [menuOpen, setMenuOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const handle = (prefs as { handle?: string } | undefined)?.handle
  const streak = useQuery(api.nodes.getUserStreakInfo, isAuthenticated ? {} : 'skip')?.streak ?? 0
  const reviewDue = useReviewDue()
  const myRank = useQuery(
    api.leaderboard.getMyLeaderboardRank,
    isAuthenticated && menuOpen ? {} : 'skip',
  )

  const { theme, setTheme } = useTheme()
  const accentPicker = useAccentPicker(() => {
    setMenuOpen(false)
    navigate({ to: '/settings' })
  })
  const { isPro } = useIsPro()
  const [currentLang, setCurrentLang] = useState(i18n.language)

  function signOut() {
    authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          clearSessionIndicator()
          // Wipe cached snapshots + queued mutations so the next user doesn't see
          // a flash of the previous account's data.
          clearOfflineData()
          navigate({ to: '/auth/login' })
        },
      },
    })
  }

  const coachInbox = useCoachInbox(showCoach && !coaching)
  const rightCount =
    (showInbox && hasInbox ? 1 : 0) +
    (showCoach && !coaching ? 1 : 0) +
    (coachInbox ? 1 : 0) +
    (showSearch ? 1 : 0)
  const sideWidth = Math.max(1, rightCount) * 32 + (rightCount > 1 ? (rightCount - 1) * 4 : 0)

  const iconProps = { className: 'h-5 w-5', strokeWidth: 1.5 }
  const navSections: {
    label?: string
    items: {
      label: string
      icon: ReactNode
      to?: string
      onClick?: () => void
      badge?: number
      dot?: boolean
      meta?: string
    }[]
  }[] = coaching
    ? [
        {
          label: t('coachingMode.section'),
          items: [
            {
              label: t('coachingMode.clients'),
              icon: <Users {...iconProps} />,
              to: '/coaching',
              meta: String(clientCount),
            },
            {
              label: t('coachingMode.plans'),
              icon: <Target {...iconProps} />,
              to: '/coaching/plans',
            },
            {
              label: t('coachingMode.library'),
              icon: <Compass {...iconProps} />,
              to: '/coaching/library',
            },
            {
              label: t('coachingMode.inviteClient'),
              icon: <UserPlus {...iconProps} />,
              onClick: () => {
                useUserMode.getState().requestInvite(true)
                navigate({ to: '/coaching' })
              },
            },
          ],
        },
        {
          items: [{ label: t('nav.settings'), icon: <Settings {...iconProps} />, to: '/settings' }],
        },
      ]
    : [
        {
          label: t('nav.sectionCommunity'),
          items: [
            {
              label: t('nav.leaderboard'),
              icon: <Trophy {...iconProps} />,
              to: '/leaderboard',
              meta: myRank ? `#${myRank.entry.rank}${myRank.capped ? '+' : ''}` : undefined,
            },
            ...(clientCount > 0
              ? [
                  {
                    label: t('nav.coaching'),
                    icon: <Users {...iconProps} />,
                    to: '/coaching',
                    meta: String(clientCount),
                  },
                ]
              : []),
          ],
        },
        {
          label: t('nav.sectionReflect'),
          items: [
            {
              label: t('coach.title'),
              icon: <CoachLauncherFace size={20} />,
              meta: coachMeta,
              // After the menu closes, so its outside-click doesn't shut the coach sheet.
              onClick: () =>
                setTimeout(() => window.dispatchEvent(new CustomEvent('open-coach')), 0),
            },
            { label: t('nav.stats'), icon: <BarChart3 {...iconProps} />, to: '/stats' },
            {
              label: t('nav.review'),
              icon: <ClipboardList {...iconProps} />,
              to: '/review',
              dot: reviewDue,
            },
          ],
        },
        {
          items: [{ label: t('nav.settings'), icon: <Settings {...iconProps} />, to: '/settings' }],
        },
      ]
  const extraItems = [
    {
      label: t('nav.inviteFriend'),
      icon: <Gift {...iconProps} />,
      onClick: () => setInviteOpen(true),
    },
    {
      label: t('nav.help'),
      icon: <LifeBuoy {...iconProps} />,
      onClick: () => window.open(HELP_URL, '_self'),
    },
    {
      label: t('nav.website'),
      icon: <Globe {...iconProps} />,
      onClick: () => window.open('https://goaltree.ai/?from=app', '_blank', 'noopener'),
    },
  ]

  function handleNavItem(item: { to?: string; onClick?: () => void }) {
    setMenuOpen(false)
    if (item.to) navigate({ to: item.to })
    else item.onClick?.()
  }

  return (
    <>
      <header
        ref={headerRef}
        data-raised={!overlay && raised ? '' : undefined}
        className={cn(
          // Line the header's edges up with the centred content column on wide screens.
          'absolute inset-x-0 top-0 z-20 flex min-h-14 items-center px-[max(1rem,calc((100%-var(--hdr-col))/2))] pt-[env(safe-area-inset-top,0px)] lg:hidden',
          overlay ? 'bg-transparent' : 'app-hdr',
        )}
        style={{ '--hdr-col': col } as CSSProperties}
      >
        <div
          data-hdr-side
          style={{ minWidth: sideWidth }}
          className="flex shrink-0 items-center gap-2"
        >
          {onBack ? (
            <IconTip label={t('common.back')}>
              <button
                type="button"
                onClick={onBack}
                className={cn(
                  'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors',
                  tightBack && '-ml-1.5',
                  transparent
                    ? 'text-white/80 hover:bg-white/10 hover:text-white'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" strokeWidth={2} />
              </button>
            </IconTip>
          ) : onClose ? (
            <IconTip label={t('common.close')}>
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors',
                  tightBack && '-ml-1.5',
                  transparent
                    ? 'text-white/80 hover:bg-white/10 hover:text-white'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
                aria-label="Close"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 5l10 10M15 5L5 15"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </IconTip>
          ) : (
            <>
              <span className="relative">
                <CanopyAvatar userId={user?._id}>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(true)}
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full overflow-hidden border border-border/40"
                  >
                    <UserAvatar
                      image={user?.image}
                      email={user?.email}
                      name={user?.name}
                      className="h-full w-full object-cover"
                    />
                  </button>
                </CanopyAvatar>
              </span>
              {leftActions}
              {isAuthenticated && (
                <IconTip label={t('nav.notifications')}>
                  <Link
                    to="/notifications"
                    className={cn(
                      'relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl transition-colors',
                      transparent
                        ? 'text-white/80 hover:bg-white/15 hover:text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      currentPath === '/notifications' && 'text-foreground',
                    )}
                    aria-label={
                      unreadCount > 0
                        ? `${t('nav.notifications')} (${unreadCount})`
                        : t('nav.notifications')
                    }
                  >
                    <Bell className="h-5 w-5" strokeWidth={1.5} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold tabular-nums text-primary-foreground ring-2 ring-background">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                </IconTip>
              )}
              <CoachingBadge />
            </>
          )}
        </div>

        <div className="flex-1" />
        <OpenAppButton hidden={scrolled} />
        <span
          className="pointer-events-none absolute inset-y-0 flex flex-col items-center justify-center pt-[env(safe-area-inset-top,0px)] transition-[opacity,transform] duration-200"
          style={{
            left: titleInset.left,
            right: titleInset.right,
            opacity: scrolled ? 1 : 0,
            transform: scrolled ? 'translateY(0)' : 'translateY(4px)',
          }}
        >
          <span className="max-w-full truncate text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {title}
          </span>
          {subtitle && <HeaderSubtitle text={subtitle} />}
        </span>

        <div
          data-hdr-side
          className="flex shrink-0 items-center justify-end gap-2"
          style={{ minWidth: rightSlot ? undefined : sideWidth }}
        >
          {rightSlot ?? (
            <>
              {actions}
              {showInbox && hasInbox && (
                <Link
                  to="/everyday"
                  onClick={() => sessionStorage.setItem('inbox_from', 'today')}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t('inbox.title')}
                >
                  <Flame className="h-5 w-5" strokeWidth={1.5} />
                </Link>
              )}
              {showCoach && <CoachHeaderButton transparent={transparent} />}
              {showSearch && (
                <IconTip label={t('nav.search')}>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-search'))}
                    className={cn(
                      'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors',
                      transparent
                        ? 'text-white/80 hover:bg-white/10 hover:text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                    aria-label={t('nav.search')}
                  >
                    <Search className="h-5 w-5" strokeWidth={1.5} />
                  </button>
                </IconTip>
              )}
            </>
          )}
        </div>
      </header>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="left"
          showCloseButton={false}
          className="flex flex-col overflow-y-auto overscroll-contain p-0"
        >
          {/* User info + notifications */}
          <div className="flex items-center gap-2 pr-4 pt-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] pb-3">
            <button
              type="button"
              onClick={() => {
                if (!user) return
                setMenuOpen(false)
                setProfileOpen(true)
              }}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 pl-4 text-left"
            >
              <CanopyAvatar userId={user?._id}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full overflow-hidden border border-border/40">
                  <UserAvatar
                    image={user?.image}
                    email={user?.email}
                    name={user?.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              </CanopyAvatar>
              <div className="min-w-0">
                {user?.name && (
                  <p className="truncate text-sm font-semibold text-foreground">{user.name}</p>
                )}
                <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                  {handle ? (
                    <span className="truncate">@{handle}</span>
                  ) : (
                    user?.email && <span className="truncate">{user.email}</span>
                  )}
                  {streak > 0 && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="shrink-0 tabular-nums">
                        <StreakFlame /> {streak}
                      </span>
                    </>
                  )}
                  {isPro && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="shrink-0 rounded-full bg-primary/15 px-1.5 py-px text-[10px] font-semibold text-primary-text">
                        {t('settings.planPro')}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </button>
            <Link
              to="/notifications"
              onClick={() => setMenuOpen(false)}
              aria-label={
                unreadCount > 0
                  ? `${t('nav.notifications')} (${unreadCount})`
                  : t('nav.notifications')
              }
              className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Bell className="h-5 w-5" strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold tabular-nums text-primary-foreground">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          </div>

          <div className="h-px bg-border/50 mx-4 mb-2" />

          {/* Search + New + Brain dump */}
          <div className="px-2 pb-1">
            <div className="flex h-11 items-center gap-0.5 rounded-xl border border-border bg-muted/40 pr-1 pl-3">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  window.dispatchEvent(new CustomEvent('open-search'))
                }}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 self-stretch text-left text-sm text-muted-foreground"
              >
                <Search className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{t('nav.searchPlaceholder')}</span>
              </button>
              <span aria-hidden className="mx-1.5 h-4 w-px bg-border" />
              <NewMenu
                align="end"
                showShortcut={false}
                onPick={() => setMenuOpen(false)}
                trigger={
                  <button
                    type="button"
                    aria-label={t('nav.new')}
                    className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted aria-expanded:bg-muted"
                  />
                }
              >
                <Plus className="h-4 w-4" strokeWidth={2.5} />
              </NewMenu>
              {!coaching && (
                <button
                  type="button"
                  aria-label={t('voice.title')}
                  onClick={() => {
                    setMenuOpen(false)
                    openCapture()
                  }}
                  className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-primary-text transition-colors hover:bg-primary/10"
                >
                  <Mic className="h-4 w-4" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>

          {/* Nav items */}
          <nav className="flex flex-1 flex-col px-2">
            {navSections.map((section, si) => (
              <div key={section.label ?? si} className={cn(si > 0 && 'mt-2')}>
                {section.label && (
                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                    {section.label}
                  </p>
                )}
                {section.items.map((item) => {
                  const { label, icon, to, badge = 0, dot, meta } = item
                  const matches = (p: string) =>
                    currentPath === p || currentPath.startsWith(p + '/')
                  const isActive =
                    !!to &&
                    matches(to) &&
                    !section.items.some((o) => !!o.to && o.to.length > to.length && matches(o.to))
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => handleNavItem(item)}
                      className={cn(
                        'relative flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors',
                        isActive
                          ? 'bg-muted font-semibold before:absolute before:inset-y-2.5 before:left-0 before:w-[3px] before:rounded-r-full before:bg-primary'
                          : 'hover:bg-muted active:bg-muted',
                      )}
                    >
                      <span className={isActive ? 'text-primary-text' : 'text-muted-foreground'}>
                        {icon}
                      </span>
                      {label}
                      {badge > 0 ? (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold tabular-nums text-primary-text">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      ) : dot ? (
                        <span
                          className="ml-auto h-2 w-2 rounded-full bg-primary"
                          aria-label={t('nav.reviewDue')}
                        />
                      ) : (
                        meta && (
                          <span className="ml-auto text-xs font-medium tabular-nums text-muted-foreground/60">
                            {meta}
                          </span>
                        )
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
            {/* Coaches: the other side of the app, in its colour. */}
            <SwitchModeButton className="mt-3" onSwitch={() => setMenuOpen(false)} />
            <div className="mx-3 my-2 h-px bg-border/50" />
            <GoProCard
              className="mb-1"
              onUpgrade={() => {
                setMenuOpen(false)
                setUpgradeOpen(true)
              }}
            />
            {extraItems.map(({ label, icon, onClick }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  onClick()
                }}
                className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted active:bg-muted"
              >
                <span className="text-muted-foreground">{icon}</span>
                {label}
              </button>
            ))}
          </nav>

          {/* Footer: theme + accent + sign out */}
          <div className="mt-auto px-4 pt-2 pb-[max(2.5rem,calc(env(safe-area-inset-bottom)+1.5rem))]">
            <div className="h-px bg-border/50 mb-4" />

            {/* Theme */}
            <div className="flex items-center gap-1 mb-3">
              {(
                [
                  {
                    id: 'light',
                    icon: <SunIcon className="h-4 w-4" />,
                    label: t('settings.themeLight'),
                  },
                  {
                    id: 'dark',
                    icon: <MoonIcon className="h-4 w-4" />,
                    label: t('settings.themeDark'),
                  },
                  {
                    id: 'system',
                    icon: <MonitorIcon className="h-4 w-4" />,
                    label: t('settings.themeSystem'),
                  },
                ] as const
              ).map(({ id, icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTheme(id)}
                  className={cn(
                    'flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition-colors',
                    theme === id
                      ? 'bg-primary/10 text-primary-text'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>

            {/* Accent */}
            <AccentSwatches
              {...accentPicker.swatches}
              size={32}
              stretch
              className="mb-3 flex-nowrap gap-2"
            />
            <div className="mb-4">
              <AccentRotateSwitch {...accentPicker.rotateSwitch} />
            </div>

            {/* Celebration */}
            <div className="mb-4 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <CelebrationChips
                size={28}
                stretch
                className="flex-1 flex-nowrap"
                onLocked={() => {
                  setMenuOpen(false)
                  setUpgradeOpen(true)
                }}
              />
            </div>

            {/* Language */}
            <div className="flex items-center gap-2 mb-4">
              <GlobeIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="flex flex-1 items-center gap-1">
                {SUPPORTED_LANGS.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage(lang)
                      localStorage.setItem(LANG_STORAGE_KEY, lang)
                      setCurrentLang(lang)
                      void setLocale({ locale: lang })
                    }}
                    className={cn(
                      'flex flex-1 cursor-pointer items-center justify-center rounded-lg py-1.5 text-[10px] font-semibold transition-colors',
                      currentLang === lang
                        ? 'bg-primary/10 text-primary-text'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    {LANG_LABELS[lang]}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-border/50 mb-3" />

            {/* Sign out */}
            <button
              type="button"
              onClick={signOut}
              className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOutIcon className="h-4 w-4 shrink-0" />
              {t('settings.signOut')}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <LeaderboardUserSheet
        userId={profileOpen && user ? (user._id as string) : null}
        name={user?.name ?? ''}
        image={user?.image ?? null}
        onClose={() => setProfileOpen(false)}
      />
      <InviteSheet open={inviteOpen} onOpenChange={setInviteOpen} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="pro" />
    </>
  )
}
